import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from '@tanstack/react-query'
import { Device } from '@twilio/voice-sdk'
import { useReadLocalStorage } from 'usehooks-ts'

import usePubNub, { Channels } from '@shared/hooks/src/usePubNub'
import useQuery from '@shared/hooks/src/useQuery'
import { queryClient, setQueryData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import useDevices from '@shared/twilio/src/hooks/useDevices'
import { SELECTED_AUDIO_INPUT_KEY, SELECTED_AUDIO_OUTPUT_KEY } from '@shared/twilio/src/utils'
import { Logger, QK, VoiceCallStatus } from '@shared/utils'

const log = Logger('VoiceCallProvider.hooks')

/**
 * Fetches a Twilio access token. A fresh token is fetched on every provider mount
 * (no cross-mount cache) and refreshed imperatively via `fetchToken()` from the
 * `tokenWillExpire` path. The token is handed straight to the Device and is not
 * cached anywhere downstream.
 */
export function useToken({ userId, externalCall }) {
  // Capture the resolved token in local state so subsequent refreshes don't
  // briefly reset `data` to undefined (which would tear down the Device).
  const [data, setData] = useState(null)

  const query = useMemo(() => ({ user_id: userId, external_user: externalCall }), [userId, externalCall])
  const enabled = Boolean(userId) || externalCall

  const { error, mutateAsync } = useMutation({
    mutationFn: async () => {
      const response = await API.calls.token(query)
      return response.token
    },
    onSuccess: (token) => setData(token),
  })

  const fetchToken = useCallback(() => mutateAsync(undefined), [mutateAsync])

  useEffect(() => {
    if (enabled) fetchToken().catch(() => {})
  }, [enabled, fetchToken])

  return { data, error, fetchToken }
}

/**
 * Fetches calls for a given patient or appointment and subscribes to call status changes.
 * Provides a function to update the call status for immediate UI feedback.
 */
export function useCall({ userId, appointmentId, externalCall, callSid, disableHistory = false }) {
  const query = useMemo(() => {
    return {
      ...(appointmentId ? { appointment_id: appointmentId } : { user_id: userId }),
      external_user: externalCall,
      order: 'desc',
      limit: 1,
    }
  }, [appointmentId, externalCall, userId])

  const call = useQuery({
    queryKey: QK.calls.list(query),
    queryFn: () => API.calls.list(query),
    enabled: !disableHistory && (Boolean(userId) || Boolean(appointmentId) || Boolean(externalCall)),
    select: (data) => data?.[0],
  })

  const updateCall = useCallback(
    (data) =>
      setQueryData(QK.calls.list(query), (prev = []) => {
        // Update the first item in the list of calls
        return [{ ...prev[0], ...data }, ...prev.slice(1)]
      }),
    [query]
  )

  // Subscribe to call status changes
  usePubNub(
    `phone_call_status_${callSid}`,
    ({ action, attributes }) => {
      if (action === Channels.CallStatusChanged) {
        const { status, start_time: startTime, end_time: endTime } = attributes
        updateCall({ status, startTime, endTime })

        if (status === VoiceCallStatus.Busy) toast.error('Call declined')
        if (status === VoiceCallStatus.NoAnswer) toast.error('No answer')
        if (status === VoiceCallStatus.Failed) toast.error('Call could not be completed')
      }
    },
    { enabled: Boolean(callSid) }
  )

  useEffect(() => {
    return () => queryClient.removeQueries({ queryKey: QK.calls.list(query) })
  }, [query])

  return [call, updateCall]
}

/**
 * Owns the Twilio Device lifecycle.
 *
 * - Creates the Device once when the initial token resolves.
 * - Refreshes the token in place via `tokenWillExpire` + `device.updateToken(...)`.
 *   The device is never destroyed for token rotation — only on unmount.
 * - Applies the user's preferred audio input/output devices.
 */
export function useTwilioDevice({ token, fetchToken, onError } = {}) {
  const [device, setDevice] = useState(null)

  const { audioInputDevices, audioOutputDevices } = useDevices()

  const selectedAudioInputId = useReadLocalStorage(SELECTED_AUDIO_INPUT_KEY)
  const selectedAudioOutputId = useReadLocalStorage(SELECTED_AUDIO_OUTPUT_KEY)

  // Refs let the create-effect (which only fires on first-token-arrival) read the
  // latest token/handlers without taking them as dependencies. Without this, a
  // token refresh would change `token`, re-fire the effect, and destroy the device.
  const tokenRef = useRef(token)
  const fetchTokenRef = useRef(fetchToken)
  const onErrorRef = useRef(onError)

  useEffect(() => {
    tokenRef.current = token
    fetchTokenRef.current = fetchToken
    onErrorRef.current = onError
  }, [token, fetchToken, onError])

  // Register the device exactly once per provider mount (when the initial token is available).
  // Re-runs only when token transitions from absent to present, never on value change.
  const hasToken = Boolean(token)
  useEffect(() => {
    if (!hasToken) return undefined

    const instance = new Device(tokenRef.current, { codecPreferences: ['opus', 'pcmu'], enableRingingState: true })

    instance.on('registered', () => {
      log.info('Voice Device registered')
      setDevice(instance)
    })

    instance.on('destroyed', () => {
      log.info('Voice Device destroyed')
    })

    instance.on('error', (error) => {
      log.error('Voice Device error', error)
      onErrorRef.current?.(error)
    })

    instance.on('tokenWillExpire', async () => {
      log.info('Voice Device token will expire — refreshing')
      try {
        const fresh = await fetchTokenRef.current?.()
        if (!fresh) return
        await instance.updateToken(fresh)
        log.info('Voice Device token refreshed')
      } catch (e) {
        log.error('Voice Device token refresh failed', e)
        toast.error('Could not refresh voice token — next call may fail')
      }
    })

    instance.register().catch((e) => {
      log.error('Voice Device could not register', e)
      onErrorRef.current?.(e)
    })

    return () => {
      log.info('Voice Device cleanup — destroying')
      instance.destroy()
    }
  }, [hasToken])

  // Apply preferred audio input/output devices whenever the available device list changes.
  useEffect(() => {
    if (!device) return

    const updateDevices = async () => {
      // Clear previous tracks before setting the new ones.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((track) => track.stop())

      if (!device.audio) return

      // Track https://github.com/twilio/twilio-voice.js/issues/104
      const inputDevice = audioInputDevices.find((d) => d.deviceId === selectedAudioInputId)
      device.audio.setInputDevice(inputDevice?.deviceId || 'default').catch(() => {
        log.warn('Could not set specified input device')
        device.audio.setInputDevice('default').catch(() => log.warn('Could not set default input device'))
      })

      const outputDevice = audioOutputDevices.find((d) => d.deviceId === selectedAudioOutputId)
      device.audio.speakerDevices.set(outputDevice?.deviceId || 'default').catch(() => {
        log.warn('Could not set specified speaker device')
        device.audio.speakerDevices.set('default').catch(() => log.warn('Could not set default speaker device'))
      })
    }

    updateDevices().catch((e) => {
      log.warn('Could not update devices', e)
      onErrorRef.current?.(e)
    })
  }, [audioInputDevices, audioOutputDevices, device, selectedAudioInputId, selectedAudioOutputId])

  return device
}

/**
 * Owns the active Twilio Call state.
 *
 * `connect` initiates an outbound call; `disconnect` ends it. After a call ends,
 * callSid is cleared on a 5s delay so trailing PubNub events still resolve to the
 * correct history row. The timer is cleared on unmount to avoid late state updates.
 */
export function useTwilioCall({ device, onError } = {}) {
  const [callSid, setCallSid] = useState('')
  const [call, setCall] = useState(null)
  const callRef = useRef(null)
  const timeoutRef = useRef(null)

  const onErrorRef = useRef(onError)
  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  const completeTheCall = useCallback((completed) => {
    completed?.removeAllListeners()
    callRef.current = null
    setCall(null)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setCallSid(''), 5000)
  }, [])

  const attachListeners = useCallback(
    (instance) => {
      instance.on('accept', (c) => {
        log.info('Voice Call received', c)
        setCall(c)
        setCallSid(c.parameters.CallSid)
      })
      instance.on('connect', (c) => {
        log.info('Voice Call connected', c)
        setCall(c)
      })
      instance.on('disconnect', (c) => {
        log.info('Voice Call disconnected', c)
        completeTheCall(c)
      })
      instance.on('cancel', (c) => {
        log.info('Voice Call cancelled', c)
        completeTheCall(c)
      })
      instance.on('error', (e) => {
        log.error('Voice Call failed', e)
        onErrorRef.current?.(e)
      })
    },
    [completeTheCall]
  )

  const connect = useCallback(
    async (to, params = {}) => {
      if (!device) return null
      const instance = await device.connect({ params: { To: to, ...params } })
      callRef.current = instance
      setCall(instance)
      attachListeners(instance)
      return instance
    },
    [device, attachListeners]
  )

  const disconnect = useCallback(() => {
    const instance = callRef.current
    if (!instance) return
    instance.disconnect()
    callRef.current = null
    setCall(null)
  }, [])

  // Clean up the post-disconnect timer and any lingering listeners on unmount.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      callRef.current?.removeAllListeners()
    }
  }, [])

  return { callSid, call, connect, disconnect }
}

export function usePatientDirectCall(patient) {
  const page = useRef()
  const disabled = !patient || patient.disabled || !patient.phone

  const makeCall = useCallback(() => {
    if (!patient) return
    if (page.current && !page.current.closed) return page.current.focus()
    page.current = undefined
    const proxy = window.open(
      `${import.meta.env.VITE_URL}/call/patient/${patient.id}`,
      `patient-direct-call-${patient.id}`,
      'popup=true,height=850,width=600'
    )
    if (!proxy) return toast.error('Please allow popups for this website')
    page.current = proxy
  }, [patient])

  return useMemo(() => {
    return { disabled, makeCall }
  }, [disabled, makeCall])
}

export function useUserDirectCall(user) {
  const page = useRef()
  const disabled = !user || !user.phone

  const makeCall = useCallback(() => {
    if (!user) return
    if (page.current && !page.current.closed) return page.current.focus()
    page.current = undefined
    const proxy = window.open(
      `${import.meta.env.VITE_URL}/call/user/${user.id}`,
      `user-direct-call-${user.id}`,
      'popup=true,height=400,width=600'
    )
    if (!proxy) return toast.error('Please allow popups for this website')
    page.current = proxy
  }, [user])

  return useMemo(() => {
    return { disabled, makeCall }
  }, [disabled, makeCall])
}
